import { Box, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { OrganizationAPI } from "../../../../api";
import AddressAPI from "../../../../api/address";
import contactInfo from "../../../../api/contactInfo";
import AddressForm from "../../../../components/address/AddressForm";
import EmailForm from "../../../../components/contact-info/EmailForm";
import PhoneForm from "../../../../components/contact-info/PhoneForm";
import Dialog from "../../../../components/Dialog";
import { useAlert } from "../../../../lib/alert";
import { Organization } from "../../../../model";
import { Address, OrganizationAddressLabels } from "../../../../model/Address";
import { ContactLabels, ContactType } from "../../../../model/Contact";
import { StripeCustomerCreation } from "../../../../model/OrganizationBillingState";

export type OrganizationStripeSetupDialogProps = {
  open: boolean;
  handleConfirm: () => Promise<void>;
  handleCancel: () => void;
  organization: Partial<Organization>;
  title?: string;
  confirmText?: string;
  cancelText?: string;
};

const OrganizationStripeSetupDialog = ({
  open,
  handleConfirm,
  handleCancel,
  organization,
  title,
  confirmText = "Save",
  cancelText = "Cancel",
}: OrganizationStripeSetupDialogProps) => {
  const [customerName, setCustomerName] = useState<string | undefined>(
    organization.key,
  );
  const [customerEmail, setCustomerEmail] = useState<string | undefined>();
  const [customerPhone, setCustomerPhone] = useState<string | undefined>();
  const [customerAddress, setCustomerAddress] = useState<Address | undefined>();

  const disableSubmit = useMemo(() => {
    if (!customerName || customerName === "") {
      return true;
    }
    if (!customerEmail || customerEmail === "") {
      return true;
    }
    if (!customerPhone || customerPhone === "") {
      return true;
    }
    if (!customerAddress || customerAddress.first_line == "") {
      return true;
    }
    if (!organization.id) {
      return true;
    }
    return false;
  }, [
    customerName,
    customerEmail,
    customerPhone,
    customerAddress,
    organization.id,
  ]);

  const { success, error } = useAlert();

  // get the organization contact information
  useEffect(() => {
    if (!organization.id) return;

    // get the email
    contactInfo
      .findOneByDetailsGlobal(
        organization?.id,
        ContactLabels.BILLING,
        ContactType.EMAIL,
      )
      .then((data) => {
        if (data) {
          setCustomerEmail(data.value);
        }
      });

    // get the phone number
    contactInfo
      .findOneByDetailsGlobal(
        organization?.id,
        ContactLabels.BILLING,
        ContactType.PHONE,
      )
      .then((data) => {
        if (data) {
          setCustomerPhone(data.value);
        }
      });

    // get the address
    AddressAPI.getOrganizationAddressByLabel(
      organization.id,
      OrganizationAddressLabels.BILLING,
    ).then((data) => {
      if (data) {
        setCustomerAddress(data);
      }
    });
  }, [organization.id, open]);

  return (
    <Dialog
      open={open}
      title={title || "Setup Stripe"}
      confirmText={confirmText}
      cancelText={cancelText}
      disableConfirm={disableSubmit}
      handleConfirm={async () => {
        if (customerName === "" || !customerName) {
          return;
        }
        if (customerEmail === "" || !customerEmail) {
          return;
        }
        if (customerPhone === "" || !customerPhone) {
          return;
        }
        if (!customerAddress || customerAddress.first_line == "") {
          return;
        }
        if (!organization.id) {
          return;
        }

        const customer: StripeCustomerCreation = {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          address: customerAddress,
        };

        await OrganizationAPI.createStripeCustomer(
          organization.id,
          customer,
        ).then(
          () => {
            success("Stripe customer created successfully");
            handleConfirm();
          },
          () => {
            error("Failed to create Stripe customer");
          },
        );
      }}
      handleCancel={handleCancel}
      maxWidth="md"
      fullWidth={true}
    >
      <Typography variant="body1">
        The details you enter will be used to create a Stripe account for your
        organization. This is required to process payments and manage
        subscriptions.
      </Typography>
      <Box my={2}>
        <TextField
          autoFocus
          margin="dense"
          id="stripe-id"
          label={`Stripe Customer Name`}
          type="text"
          fullWidth
          variant="standard"
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value as string)}
          sx={{
            flex: 1,
          }}
          error={customerName === ""}
          required={true}
        />
        <EmailForm
          margin="dense"
          id="billing-email"
          label={`Stripe Customer Billing Email`}
          type="email"
          fullWidth
          variant="standard"
          defaultValue={customerEmail}
          onChange={(e) => setCustomerEmail(e)}
          sx={{
            flex: 1,
          }}
          error={customerEmail === ""}
        />
        {/* <TextField
          margin="dense"
          id="billing-email"
          label={`Stripe Customer Billing Email`}
          type="email"
          fullWidth
          variant="standard"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value as string)}
          sx={{
            flex: 1,
          }}
          required={true}
        /> */}
        <PhoneForm
          margin="dense"
          id="phone"
          label={`Stripe Customer Phone`}
          type="tel"
          fullWidth
          variant="standard"
          defaultValue={customerPhone}
          onChange={(e) => setCustomerPhone(e)}
          sx={{
            flex: 1,
          }}
          error={customerPhone === ""}
        />
        {/* <TextField
          margin="dense"
          id="phone"
          label={`Stripe Customer Phone`}
          type="tel"
          fullWidth
          variant="standard"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value as string)}
          sx={{
            flex: 1,
          }}
          required={true}
        /> */}
        {open && (
          <Box mt={1}>
            <AddressForm
              strict
              onChange={(address) => {
                setCustomerAddress(address);
              }}
              condensed={true}
              autocompleteLabel="Stripe Customer Billing Address"
              variant="standard"
              canExpand={true}
              noControls={true}
              current={customerAddress}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default OrganizationStripeSetupDialog;
